import { createSelector } from 'reselect'
import { objectsToArray, digObject, sortArrayBy } from 'functions/utilities'

import multiFilteredObjectArraySelector from 'functions/multiFilteredObjectArraySelector'

const getState = state => state

const getAgency = (dataSourceType, dataSource, agencies) => {
  if (dataSourceType === 'DigitalPage' && dataSource.owner_type === 'Agency'){
    return agencies[dataSource.owner_id] || {}
  }

  if (dataSourceType === 'CampaignProposal'){
    return agencies[dataSource.agency_id] || {}
  }

  return {}
}

const getCampaign = (dataSourceType, dataSource, campaigns) => {
  if (dataSourceType === 'DigitalPage' && dataSource.subject_type === 'Campaign'){
    return campaigns[dataSource.subject_id] || {}
  }

  if (dataSourceType === 'CampaignProposal'){
    return campaigns[dataSource.campaign_id] || {}
  }

  return {}
}

const filterAttachments = (dataSourceType, dataSource, attachments) => {
  const array = objectsToArray(attachments)
  return array.filter(item => item.attachable_type === dataSourceType && item.attachable_id === dataSource.id)
}

const filterAdCopies = (campaign, adCopies) => {
  const filteredAdCopies = Object.values(adCopies).filter(adCopy => adCopy.campaign_id === campaign.id)
  return filteredAdCopies.reduce((obj, adCopy) => {
    const { copy_type, description, tagline } = adCopy || {}

    obj[copy_type] = {
      description,
      tagline,
    }

    return obj
  }, {})
}

const filterCampaignDocuments = (campaign, campaignDocuments) => {
  const array = objectsToArray(campaignDocuments)
  return array.filter(item => item.campaign_id === campaign.id)
}

const filterCampaignComparables = (campaign, campaignComparables) => {
  const array = objectsToArray(campaignComparables)

  return array.filter(item => item.campaign_id === campaign.id)
}

const filterCampaignQuotes = (campaign, dataSource, campaignQuotes) => {
  const quoteIds = digObject(dataSource, 'options.campaign_quote_ids', [])

  if (quoteIds.length){
    return quoteIds.map(quoteId => campaignQuotes[quoteId]).filter(q => q)
  }

  return Object.values(campaignQuotes).filter(
    quote => quote.campaign_id === campaign.id,
  )
}

const filterDigitalTemplates = (dataSourceType, dataSource, digitalTemplates) => {
  const array = objectsToArray(digitalTemplates)
  const { page_type } = dataSource
  const proposalTypes = ['proposal', null]
  const isProposal = dataSourceType === 'CampaignProposal' || page_type === 'proposal'
  const permittedTypes = isProposal ? proposalTypes : [page_type]

  return array.filter((item) => {
    const templateType = digObject(item, 'options.template_type', null)

    return permittedTypes.includes(templateType) && !item.hidden
  })
}

const filterDocumentSigners = (documentGroup, documentSigners) => {
  const array = Object.values(documentSigners)
  const filtered = array.filter(({ signable_type, signable_id }) => signable_type === 'DocumentGroup' && signable_id === documentGroup.id)
  return filtered
}

export const getLatestAttachment = (attachments, filters) => {
  const filtered = multiFilteredObjectArraySelector(
    { entities: { attachments } },
    'attachments',
    filters,
  )
  const sorted = sortArrayBy(filtered, 'desc', 'id')

  return sorted[0] || {}
}

const relatedEntitySelector = createSelector(
  [getState],
  (state) => {
    const {
      adCopies,
      agencies,
      artworks,
      attachments,
      brands,
      campaignComparables,
      campaignDocuments,
      campaignProposals,
      campaignQuotes,
      campaignSaleMethods,
      campaigns,
      digitalPages,
      digitalTemplatePages,
      digitalTemplates,
      documentGroups,
      documentSigners,
      documents,
      geoSuburbs,
    } = state.entities

    const campaignProposal = campaignProposals[state.campaignProposals.selectedId] || {}
    const digitalPage = digitalPages[state.digitalPages.selectedId] || {}

    const dataSourceType = digitalPage.id ? 'DigitalPage' : 'CampaignProposal'
    const rawDataSource = digitalPage.id ? digitalPage : campaignProposal
    const dataSource = {
      ...rawDataSource,
      subject_type: rawDataSource.subject_type || 'Campaign',
      subject_id: rawDataSource.subject_id || rawDataSource.campaign_id,
    }

    const agency = getAgency(dataSourceType, dataSource, agencies)
    const artwork = artworks[dataSource.artwork] || {}
    const brand = brands[agency.brand_id] || {}
    const campaign = getCampaign(dataSourceType, dataSource, campaigns)
    const campaignSaleMethod = campaignSaleMethods[campaign.campaign_sale_method_id] || {}
    const documentGroup = documentGroups[digitalPage.document_group_id] || {}
    const suburb = geoSuburbs[campaign.suburb_id] || {}

    const digitalTemplate = digitalTemplates[dataSource.digital_template_id] || {}
    const firstPage = digitalTemplate.pages ? digitalTemplate.pages[0] : null
    const digitalTemplatePage = digitalTemplatePages[firstPage] || {}

    const filteredAttachments = filterAttachments(dataSourceType, dataSource, attachments)

    const pdfDownloadFilters = [
      { key: 'attachable_type', value: dataSourceType },
      { key: 'attachable_id', value: dataSource.id },
      { key: 'attachment_type', value: 'pdf_file' },
    ]

    const environment = {
      adCopies: filterAdCopies(campaign, adCopies),
      agency,
      artwork,
      attachments: filteredAttachments,
      brand,
      campaign,
      campaignComparables: filterCampaignComparables(campaign, campaignComparables),
      campaignDocuments: filterCampaignDocuments(campaign, campaignDocuments),
      campaignProposal,
      campaignSaleMethod,
      campaignQuotes: filterCampaignQuotes(campaign, dataSource, campaignQuotes),
      dataSource,
      dataSourceType,
      digitalPage,
      digitalTemplate,
      digitalTemplatePage,
      digitalTemplates: filterDigitalTemplates(dataSourceType, dataSource, digitalTemplates),
      documentGroup,
      documentSigners: filterDocumentSigners(documentGroup, documentSigners),
      documents,
      pdfAttachment: getLatestAttachment(filteredAttachments, pdfDownloadFilters),
      pdfDownloadFilters,
      suburb,
    }

    return environment
  },
)

export default relatedEntitySelector
